<template>
	<div id="app" v-preloader="isInlineLoading" class="app" :class="globalClasses">
		<app-cover :is-visible="!isAtLeastTablet" />

		<layout-system :show="isAtLeastTablet" :default-layout="LayoutDefault" />

		<app-loader v-if="isLoading && isAtLeastTablet" />
	</div>
	<!-- should be displayed only if displayTermsAndCondtionsModal === true -->
	<app-dialog
		v-if="displayTermsAndCondtionsModal"
		:require-action="true"
		:is-open="displayTermsAndCondtionsModal"
		class="terms-and-conditions-dialog"
	>
		<TermsAndConditionsPopup
			:on-accept="onAccept"
			:on-reject="onReject"
			:is-loading="isAcceptingTermsAndConditions"
		/>
	</app-dialog>

	<popup-root>
		<vue-toast-system />
		<vue-dialog-system />
	</popup-root>
</template>

<script lang="ts">
import { computed, defineComponent, ref } from 'vue';

import LayoutSystem from '@/common/layout-system/LayoutSystem.vue';
import { PopupRoot } from '@/common/popup';
import { openToast, VueDialogSystem, VueToastSystem } from '@/common/popup-systems';
import AppCover from '@/components/app/AppCover.vue';
import AppLoader from '@/components/app/AppLoader.vue';
import { useMediaQuery, useViewUnits } from '@/composables';
import { useSyncUserAbilities } from '@/composables/app';
import LayoutDefault from '@/layouts/LayoutDefault.vue';
import { fetchProstheses, handleNotifications } from '@/mixins/app';
import { authActions, authGetters } from '@/store/modules/auth';
import { globalState } from '@/store/modules/global';
import { isSafari } from '@/tools/helpers';

import AppDialog, {
	PREVENT_SCROLLING_CLASS,
} from './common/popup-systems/dialog/components/AppDialog.vue';
import TermsAndConditionsPopup from './components/termsAndConditions/TermsAndConditionsPopup.vue';
import { ERRORS } from './constants/errors/constants';
import { UserType } from './constants/users/constants';
import api from './tools/api';

export default defineComponent({
	components: {
		AppLoader,
		AppCover,
		LayoutSystem,
		PopupRoot,
		VueToastSystem,
		VueDialogSystem,
		AppDialog,
		TermsAndConditionsPopup,
	},

	mixins: [handleNotifications, fetchProstheses],

	setup() {
		useViewUnits();

		const isAtLeastTablet = useMediaQuery([
			{ width: [['min', 'md']], orientation: 'portrait' },
			{ width: [['min', 'lg']], orientation: 'landscape' },
		]);

		const displayTermsAndCondtionsModal = ref(false);
		const isAcceptingTermsAndConditions = ref(false);

		const onAccept = async () => {
			isAcceptingTermsAndConditions.value = true;
			await api
				.post('/users/accept-terms-and-conditions')
				.then(() => {
					displayTermsAndCondtionsModal.value = false;
					document.body.classList.remove(PREVENT_SCROLLING_CLASS);
				})
				.catch(() => openToast({ message: ERRORS.GENERIC_ERROR_MESSAGE }))
				.finally(() => (isAcceptingTermsAndConditions.value = false));
		};

		const onReject = async () => {
			await authActions.logout();
			displayTermsAndCondtionsModal.value = false;
			document.body.classList.remove(PREVENT_SCROLLING_CLASS);
		};

		return {
			...useSyncUserAbilities(),

			LayoutDefault,
			isAtLeastTablet,
			displayTermsAndCondtionsModal,
			isAcceptingTermsAndConditions,
			globalClasses: {
				'is-safari': isSafari(),
			},

			externalLoading: computed(globalState.showGlobalLoader),
			onAccept,
			onReject,
		};
	},

	computed: {
		...authGetters.map(['isAuthorized', 'currentUser']),

		isLoading(): boolean {
			return this.$isLoading(['auth/fetchCurrentUser', 'auth/logout']);
		},

		isFetching(): boolean {
			return this.$isLoading(['prosthesis/fetchProsthesisList']);
		},

		isInlineLoading(): boolean {
			return !this.isLoading && (this.externalLoading || this.$isLoading(['auth/refreshToken']));
		},
	},

	watch: {
		isAuthorized() {
			if (!this.isAuthorized && !this.$route?.meta?.requireNoAuth) {
				this.$router.push({ name: 'login' });
			}
		},
		currentUser() {
			if (
				this.currentUser &&
				this.currentUser.role == UserType.prosthetist &&
				this.currentUser.accepted_terms_and_conditions === false
			) {
				this.displayTermsAndCondtionsModal = true;
			}
		},
	},
});
</script>

<style lang="scss">
:root {
	--vh: 1vh;
}

.app {
	display: flex;
	align-items: stretch;
	min-height: $view-height;
	line-height: 1.5;
}

.terms-and-conditions-dialog {
	.app-dialog__content {
		width: 80vw;
		max-width: 1200px;
	}
}
</style>
